import { noop } from "@tanstack/react-query"
import { redirect, type RedirectOptions, type RegisteredRouter } from "@tanstack/react-router"

import { hasPermission } from "@/api/hooks/useRole"
import { query } from "@/api/query"
import { policiesQuery } from "@/api/query/policies"
import { MY_SPACE } from "@/constants/spaces"
import type { RouterContext } from "@/router"

/**
 * A route guard that performs preloading checks for routes that
 * require authentication.
 *
 * @param context - Context object.
 * @param config - Configuration options.
 * @returns
 * @throws {Error} - If the user is not logged in.
 * @throws {Redirect} - If the user is a minor and the route does not allow minors.
 * @throws {Redirect} - If the user is in a customized environment and the route does not allow customization.
 * @throws {Redirect} - If the user's role lacks the required `permission` flag: to `permission.fallbackRoute`, or `/` when omitted.
 */
export async function routeGuard<const TTo extends string | undefined = undefined>(
  context: RouterContext,
  config: {
    allowMinor?: boolean
    allowPrivateSpace?: boolean
    allowCustomized?: boolean
    preload?: boolean
    /**
     * Require an explicit RBAC flag to enter the route (e.g. `"iot:thing::create"`).
     * `fallbackRoute` is where a role without the flag lands, usually the closest route it can still
     * open (e.g. the device page for its firmware update). Defaults to `/`.
     */
    permission?: {
      flag: Parameters<typeof hasPermission>[0]["permission"]
      fallbackRoute?: RedirectOptions<RegisteredRouter, string, TTo>
    }
  } = {}
) {
  // Preload is currently not used, but we may need it in the future
  const { allowMinor = false, allowPrivateSpace = true, allowCustomized = true, preload: _preload = false } = config

  const spaceId = context.spaceStorage.getSpace()

  const user = await context.authClient.getUser()
  if (!user) {
    throw new Error("You must be logged in to access this page.")
  }

  // Block access if minor
  if (!allowMinor && user["http://arduino.cc/is_minor"] === true) {
    redirect({
      to: "/",
      throw: true
    })
  }

  // Block access if in a customized environment
  if (!allowCustomized && context.authClient.getCustomization()) {
    redirect({
      to: "/",
      throw: true
    })
  }

  if (!allowPrivateSpace && spaceId === MY_SPACE.id) {
    redirect({
      to: "/",
      throw: true
    })
  }

  // RBAC: block access if the current role doesn't have the required permission flag.
  if (config.permission) {
    const spaceList = await context.queryClient.query({ ...query.space.list, staleTime: "static" })
    const space = spaceList.find(s => s.id === spaceId && s.id !== MY_SPACE.id)
    if (space) {
      const options = policiesQuery(space.id)
      // The root and the space switch own fetching: use the loaded matrix, or wait for one still in flight.
      // A failed one has no data, so it would count as stale and refetch on every gated navigation: skip it.
      const failed = context.queryClient.getQueryState(options.queryKey)?.status === "error"
      const permissionMatrix = failed ? undefined : await context.queryClient.query(options).catch(noop)

      if (
        !hasPermission({
          matrix: permissionMatrix || { policies: {} },
          role: space.roles[0],
          permission: config.permission.flag
        })
      ) {
        if (config.permission.fallbackRoute) throw redirect(config.permission.fallbackRoute)
        redirect({
          to: "/",
          throw: true
        })
      }
    }
  }
}
